import { HTMLAttributes, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';

import {
  AspectRatio,
  CameraInfo,
  InventoryType,
  WinchCalibrationState,
} from '@scaleaq/sitecontrol-lib';
import { useTranslation } from 'react-i18next';

import {
  hasCameraControlActions,
  hasCameraZoomActions,
} from '@/components/cameraStreamWidget/cameraStreamWidgetWrapper/cameraControl.help.ts';
import type { CameraMovementFeedbackKind } from '@/components/cameraStreamWidget/cameraStreamWidgetWrapper/cameraMovementFeedback/cameraMovementFeedback.types.ts';
import { useCameraAndWinchMovement } from '@/components/cameraStreamWidget/cameraStreamWidgetWrapper/useCameraAndWinchCommandHooks.ts';
import type { CameraSwitching } from '@/components/cameraStreamWidget/cameraSwitchPanel/cameraSwitchPanel.types.ts';
import type { HoldRepeatWhileKeyHeldBinding } from '@/hooks/shortcutHooks/holdRepeatWhileKeyHeld.ts';
import {
  CameraControlHotkeyNames,
  WinchControlHotkeyNames,
} from '@/hooks/shortcutHooks/hotkeys/types.ts';
import { useCameraControlHotkeys } from '@/hooks/shortcutHooks/useCameraControlHotkeys.ts';
import { useCameraControlHotKeyDefinitions } from '@/hooks/shortcutHooks/useHotkeyDefinitions/useCameraControlHotKeyDefinitions.tsx';
import { useWinchControlHotkeysDefinitions } from '@/hooks/shortcutHooks/useHotkeyDefinitions/useWinchControlHotkeysDefinitions.tsx';
import { buttonArguments, JoystickConfigMap, useGamepadContext } from '@/providers';
import { GamepadLayout } from '@/providers/gamepadProvider/gamepadLayout';
import { CAMERA_STREAM_WIDGET } from '@/widgetComponentNames';

import { borderRadius, CustomStyledCardWrapperWithState } from './cameraStreamWidget.styles';
import {
  CameraMovementFeedbackOverlay,
  type CameraMovementFeedbackOverlayHandle,
} from './cameraStreamWidgetWrapper/CameraMovementFeedbackOverlay.tsx';
import { CameraRadialControls } from './cameraStreamWidgetWrapper/CameraRadialControls.tsx';
import { CameraStreamFrameControlsProvider } from './cameraStreamWidgetWrapper/CameraStreamFrameControlsProvider.tsx';

type CameraStreamWidgetWrapperProps = HTMLAttributes<HTMLDivElement> & {
  camera: CameraInfo;
  isError: boolean;
  error?: string;
  isLoading: boolean;
  isVideoPaused?: boolean;
  state: string;
  loadingZIndex: number;
  aspectRatio: AspectRatio;
  /** Camera (PTZ) controls + hotkeys + gamepad. Hidden during HLS rewind (default true). */
  showCameraControls?: boolean;
  /** When true, video top banner (controls strip) stays visible without hover. */
  topBannerPinned?: boolean;
  /** The F key toggles fullscreen when the widget has focus and this is set. */
  onToggleFullscreen?: (element: HTMLDivElement) => void;
  /** Camera group switching (hotkeys, gamepad). Omitted ⇒ neither shortcut is registered. */
  cameraSwitching?: CameraSwitching;
};
const callInterval = 1000;

export const CameraStreamWidgetWrapper = ({
  camera,
  aspectRatio,
  children,
  isError,
  error,
  isLoading,
  isVideoPaused = false,
  state,
  loadingZIndex,
  showCameraControls = true,
  topBannerPinned = false,
  onToggleFullscreen,
  cameraSwitching,
  ...rest
}: CameraStreamWidgetWrapperProps) => {
  const { t } = useTranslation('cameraControl');
  const [isActive, setIsActive] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const cameraWidgetId = camera.id ?? camera.name;
  // A layout can show the same camera in several tiles. Key the gamepad map per tile so an
  // unfocused tile never unregisters the map of a focused tile that shows the same camera.
  const gamepadWidgetId = `${cameraWidgetId}:${useId()}`;

  const hasWinch = useMemo(() => Boolean(camera?.winchInfo), [camera?.winchInfo]);
  // The winch can't be driven in maintenance mode or while a smart winch is calibrating.
  const winchInfo = camera?.winchInfo;
  const isWinchUnavailable =
    Boolean(winchInfo?.isInMaintenanceMode) ||
    (winchInfo?.winchType === InventoryType.SmartWinch &&
      winchInfo.calibration?.state === WinchCalibrationState.Running);
  const canRotate = useMemo(() => hasCameraControlActions(camera), [camera]);
  const canZoom = useMemo(() => hasCameraZoomActions(camera), [camera]);

  const { registerWidgetMap, unregisterWidgetMap, setActiveWidgetId } = useGamepadContext();

  const { cameraControlHotKeys: allCameraControlHotKeys, cameraControlHoldRepeatHotkeys } =
    useCameraControlHotKeyDefinitions();

  const onSwitchCamera = cameraSwitching?.onOpenSelector;
  // A group of one has nothing to cycle to, so that shortcut stays unregistered.
  const onCycleCamera =
    cameraSwitching && cameraSwitching.siblings.length > 1 ? cameraSwitching.onCycle : undefined;

  // Drop hotkeys without a handler (e.g. non-landing-page widget) to avoid confusion.
  const cameraControlHotKeys = useMemo(() => {
    const {
      [CameraControlHotkeyNames.SwitchCamera]: switchCamera,
      [CameraControlHotkeyNames.CycleCamera]: cycleCamera,
      ...rest
    } = allCameraControlHotKeys;
    return {
      ...rest,
      ...(onSwitchCamera ? { [CameraControlHotkeyNames.SwitchCamera]: switchCamera } : {}),
      ...(onCycleCamera ? { [CameraControlHotkeyNames.CycleCamera]: cycleCamera } : {}),
    };
  }, [allCameraControlHotKeys, onSwitchCamera, onCycleCamera]);
  const winchControlHotkeys = useWinchControlHotkeysDefinitions();
  const movementFeedbackOverlayRef = useRef<CameraMovementFeedbackOverlayHandle>(null);
  const onMovementSuccess = useCallback((kind: CameraMovementFeedbackKind) => {
    movementFeedbackOverlayRef.current?.trigger(kind);
  }, []);

  const movement = useCameraAndWinchMovement(
    camera,
    {
      cameraControlHoldRepeatHotkeys,
      winchControlHotkeys,
    },
    { onMovementSuccess }
  );

  const {
    // camera rotation
    startCameraRotation,
    stopAllCameraRotation,
    onCameraPanOrTilt,
    onStopCameraRotation,

    // winch movement
    on2DWinchMove,
    startWinch,
    stopWinchAll,
    onMoveWinchStop,
  } = movement;

  const holdRepeat = useMemo(() => {
    const bindings: HoldRepeatWhileKeyHeldBinding[] = [];
    if (canRotate) {
      for (const name of [
        CameraControlHotkeyNames.TiltCameraUp,
        CameraControlHotkeyNames.TiltCameraDown,
        CameraControlHotkeyNames.PanCameraLeft,
        CameraControlHotkeyNames.PanCameraRight,
      ] as const) {
        const hk = cameraControlHoldRepeatHotkeys[name];
        const key = hk.primary.keyboardKey;
        bindings.push({
          hotkey: hk,
          onTick: () => {
            startCameraRotation[key]?.();
          },
          onRelease: () => {
            stopAllCameraRotation[key]?.();
          },
        });
      }
    }
    if (hasWinch && !isWinchUnavailable) {
      for (const name of [
        WinchControlHotkeyNames.MoveWinchUp,
        WinchControlHotkeyNames.MoveWinchDown,
        WinchControlHotkeyNames.MoveWinchIn,
        WinchControlHotkeyNames.MoveWinchOut,
      ] as const) {
        const hk = winchControlHotkeys[name];
        const key = hk.primary.keyboardKey;
        bindings.push({
          hotkey: hk,
          onTick: () => {
            startWinch[key]?.();
          },
          onRelease: () => {
            stopWinchAll[key]?.();
          },
        });
      }
    }
    if (!bindings.length) return undefined;
    return {
      intervalMs: callInterval,
      callOnFirstTick: true,
      bindings,
    };
  }, [
    canRotate,
    hasWinch,
    isWinchUnavailable,
    cameraControlHoldRepeatHotkeys,
    winchControlHotkeys,
    startCameraRotation,
    stopAllCameraRotation,
    startWinch,
    stopWinchAll,
  ]);

  useCameraControlHotkeys({
    scopeRef: wrapperRef,
    enabled: isActive && showCameraControls,
    onToggleFullscreen: onToggleFullscreen,
    onSwitchCamera,
    onCycleCamera,
    cameraControlHotKeys,
    holdRepeat: isVideoPaused ? undefined : holdRepeat,
  });

  /**
   * Widget-level gamepad: sibling camera cycling, pan/tilt, and winch — requires focus on this card.
   * **L2/R2 (prev/next camera stream)** stay in `GamepadProvider`: they must work while fullscreen with
   * gamepad-only use (no reliable `scopeRef` / focus), so they call `gamepadNavigateBetweenCameraStreams`
   * which shares the same navigate helper as Alt+arrow.
   */
  // eslint-disable-next-line react-x/no-unnecessary-use-memo
  const widgetMaps: JoystickConfigMap = useMemo(
    () => ({
      // camera pan/tilt (if camera can rotate)
      ...(canRotate
        ? {
            [GamepadLayout.camera.cameraPanTilt]: {
              handler: (args: buttonArguments) => {
                if (!args) {
                  console.error('Invalid joystick input for camera pan/tilt, expected x/y values.');
                  return;
                }
                // Gamepad API: stick down → positive Y; camera/winch APIs use positive = up (keyboard).
                onCameraPanOrTilt({
                  pan: args.x,
                  tilt: -args.y,
                });
              },
              stopHandler: () => {
                onStopCameraRotation();
              },
              repeat: true,
              intervalMs: callInterval,
            },
          }
        : {}),

      ...(onCycleCamera
        ? {
            [GamepadLayout.camera.cycleCamera]: {
              handler: () => onCycleCamera(),
              repeat: false,
            },
          }
        : {}),

      // winch (if present and not in maintenance)
      ...(hasWinch && !isWinchUnavailable
        ? {
            [GamepadLayout.winch.moveWinch2D]: {
              handler: (args: buttonArguments) => {
                if (!args) {
                  console.error('Invalid joystick input for winch movement, expected x/y values.');
                  return;
                }
                on2DWinchMove(args.x, -args.y);
              },
              stopHandler: () => {
                onMoveWinchStop();
              },
              repeat: true,
              intervalMs: callInterval,
            },
          }
        : {}),
    }),
    [
      canRotate,
      hasWinch,
      isWinchUnavailable,
      onCameraPanOrTilt,
      onStopCameraRotation,
      onCycleCamera,
      on2DWinchMove,
      onMoveWinchStop,
    ]
  );

  // Stop pan/tilt/winch when the stream is paused (keyboard hold-repeat or gamepad may be mid-move).
  useEffect(() => {
    if (!isVideoPaused) return;
    onStopCameraRotation();
    onMoveWinchStop();
  }, [isVideoPaused, onMoveWinchStop, onStopCameraRotation]);

  // Dropping the bindings above does not stop a move that is already in progress.
  useEffect(() => {
    if (!isWinchUnavailable) return;
    onMoveWinchStop();
  }, [isWinchUnavailable, onMoveWinchStop]);

  // Release the active slot only if this tile still holds it. Read the latest state through the
  // updater: when focus moves back to an earlier tile, its effect runs first, so a render-time
  // snapshot here would still name the tile being left and clear the tile just focused.
  useEffect(() => {
    const releaseActive = () =>
      setActiveWidgetId(current => (current === gamepadWidgetId ? null : current));

    if (
      !isActive ||
      isVideoPaused ||
      !showCameraControls ||
      (!canRotate && !hasWinch && !onCycleCamera)
    ) {
      releaseActive();
      return;
    }

    setActiveWidgetId(gamepadWidgetId);

    return releaseActive;
  }, [
    isActive,
    isVideoPaused,
    showCameraControls,
    setActiveWidgetId,
    gamepadWidgetId,
    canRotate,
    hasWinch,
    onCycleCamera,
  ]);

  // Register/unregister widget gamepad map based on focus/active state.
  useEffect(() => {
    if (!isActive || isVideoPaused || !showCameraControls) {
      unregisterWidgetMap(gamepadWidgetId);
      return;
    }

    if (!canRotate && !hasWinch && !onCycleCamera) {
      return;
    }

    registerWidgetMap(gamepadWidgetId, widgetMaps);

    return () => {
      unregisterWidgetMap(gamepadWidgetId);
    };
  }, [
    isActive,
    isVideoPaused,
    showCameraControls,
    widgetMaps,
    registerWidgetMap,
    unregisterWidgetMap,
    gamepadWidgetId,
    canRotate,
    hasWinch,
    onCycleCamera,
  ]);
  const showRadialControls =
    showCameraControls &&
    (canRotate || hasWinch || canZoom) &&
    !isLoading &&
    !isError &&
    !isVideoPaused;

  return (
    <CustomStyledCardWrapperWithState
      {...rest}
      $aspectRatio={aspectRatio}
      $topBannerPinned={topBannerPinned}
      loadingZIndex={loadingZIndex}
      hideBorder
      data-component={CAMERA_STREAM_WIDGET}
      data-camera-id={cameraWidgetId}
      data-camera-stream-connected={state === 'connected' ? 'true' : undefined}
      elevation='none'
      borderRadius={borderRadius}
      isError={isError}
      errorMessage={error}
      isLoading={(isLoading || state === 'connecting') && !isVideoPaused}
      isEmpty={isVideoPaused}
      emptyMessages={[t('videoPaused')]}
      tabIndex={0}
      ref={wrapperRef}
      onPointerDownCapture={event => {
        // Overlay clicks hit a button/sector, not this card. Pan/tilt also preventDefault,
        // which would leave focus on the widget the pointer just left. Take focus first.
        if (event.button === 0) wrapperRef.current?.focus({ preventScroll: true });
      }}
      onFocus={() => setIsActive(true)}
      onBlur={e => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
          setIsActive(false);
        }
      }}
    >
      <CameraStreamFrameControlsProvider scopeRef={wrapperRef}>
        {children}
        {/* One icon flash per successful movement POST (keyboard, radial pad, gamepad). */}
        <CameraMovementFeedbackOverlay
          ref={movementFeedbackOverlayRef}
          zIndex={loadingZIndex + 1}
        />
        {showRadialControls && (
          <CameraRadialControls
            camera={camera}
            canRotate={canRotate}
            canZoom={canZoom}
            hasWinch={hasWinch}
            zIndex={loadingZIndex + 2}
            holdRepeatDelayMs={callInterval}
            movement={movement}
            isWinchUnavailable={isWinchUnavailable}
          />
        )}
      </CameraStreamFrameControlsProvider>
    </CustomStyledCardWrapperWithState>
  );
};
